/**
 * NextActionCard - Prominent Next Action Highlight
 * Displays the single most important task to work on right now
 * Enhanced with deadline sprint mode awareness
 */

import React from 'react';
import { Target, Clock, Sparkles, ArrowRight, Users, Zap, Calendar } from 'lucide-react';
import {
  getNextAction,
  formatDueDate,
  getDaysUntilDue,
  getTimeEstimate,
  formatTimeEstimate,
  isOverdue
} from '../../utils/taskUtils';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';

const NextActionCard = ({
  tasks = [],
  taskSubtasksMap = {},
  colleges = [],
  onStartClick,
  onAISupportClick,
  className = ''
}) => {
  // Get deadline context for sprint mode
  const deadlineInfo = DeadlineIntelligenceService.getCurrentPhase(colleges);
  const phase = deadlineInfo?.phase || 'NORMAL';
  const isSprintMode = phase === 'EMERGENCY_7' || phase === 'SPRINT_14' || phase === 'CRUNCH_30';

  // Pass deadline context to getNextAction for smart prioritization
  const deadlineContext = isSprintMode ? {
    phase: phase,
    college_id: deadlineInfo?.college_id,
    daysUntil: deadlineInfo?.daysUntil
  } : null;

  const nextAction = getNextAction(tasks, taskSubtasksMap, deadlineContext);

  if (!nextAction) {
    return (
      <div className={`bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Target className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-green-900 mb-1">
          All caught up!
        </h3>
        <p className="text-sm text-green-700">
          No immediate actions needed. Great work!
        </p>
      </div>
    );
  }

  const { parentTask, subtask, isFoundation, unlockCount, deadlinePhase, daysUntilDeadline } = nextAction;
  const overdue = isOverdue(subtask);
  const daysUntil = getDaysUntilDue(subtask);
  const dueDateText = formatDueDate(subtask.due_date);
  const timeEstimate = getTimeEstimate(subtask);
  const taskTip = subtask.task_tip || subtask.metadata?.task_tip;

  // Sprint mode styling
  const isEmergency = phase === 'EMERGENCY_7';
  const isSprint = phase === 'SPRINT_14';
  const isCrunch = phase === 'CRUNCH_30';

  const cardColor = isEmergency
    ? 'from-red-50 to-orange-50 border-red-300'
    : isSprint
      ? 'from-orange-50 to-yellow-50 border-orange-300'
      : isCrunch
        ? 'from-yellow-50 to-amber-50 border-yellow-300'
        : 'from-indigo-50 to-purple-50 border-indigo-200';

  const iconColor = isEmergency
    ? 'bg-red-500'
    : isSprint
      ? 'bg-orange-500'
      : isCrunch
        ? 'bg-yellow-500'
        : 'bg-indigo-500';

  const textColor = isEmergency
    ? 'text-red-700'
    : isSprint
      ? 'text-orange-700'
      : isCrunch
        ? 'text-yellow-700'
        : 'text-indigo-700';

  const Icon = isSprintMode ? Zap : Target;

  return (
    <div className={`bg-gradient-to-br ${cardColor} border-2 rounded-lg shadow-lg ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 ${iconColor} rounded-lg flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-medium ${textColor} uppercase tracking-wide`}>
              {isSprintMode ? `${deadlineInfo.college} Sprint - ${daysUntilDeadline} Days` : 'Your Next Action'}
            </h3>
            <p className={`text-xs ${textColor.replace('700', '600')}`}>
              {isSprintMode
                ? `Focus on ${deadlineInfo.college} ${deadlineInfo.round?.replace('_', ' ').toUpperCase()}`
                : 'Focus here to make the most progress'
              }
            </p>
          </div>
        </div>

        {/* Sprint Mode Context Banner */}
        {isSprintMode && (
          <div className={`mb-4 p-3 rounded-lg border ${
            isEmergency
              ? 'bg-red-100 border-red-300'
              : isSprint
                ? 'bg-orange-100 border-orange-300'
                : 'bg-yellow-100 border-yellow-300'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wide">
                {isEmergency ? '🚨 EMERGENCY MODE' : isSprint ? '⚡ SPRINT MODE' : '📅 CRUNCH TIME'}
              </span>
            </div>
            <p className="text-xs leading-relaxed">
              {isEmergency && `Only ${daysUntilDeadline} days until ${deadlineInfo.college} deadline. All focus on this application now.`}
              {isSprint && `${daysUntilDeadline} days to ${deadlineInfo.college} deadline. Prioritizing critical tasks for this school.`}
              {isCrunch && `${daysUntilDeadline} days to ${deadlineInfo.college}. Building momentum - focus on deadline schools.`}
            </p>
          </div>
        )}

        {/* Parent Task Context */}
        <div className="mb-3">
          <p className="text-xs text-gray-600 mb-1">Part of:</p>
          <p className="text-sm font-medium text-gray-700">
            {parentTask.title}
          </p>
        </div>

        {/* Subtask Title */}
        <h4 className="text-xl font-bold text-gray-900 mb-3">
          {subtask.friendly_title || subtask.title}
        </h4>

        {/* Badges Row */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {/* Foundation Badge */}
          {isFoundation && unlockCount > 0 && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              <Users className="w-3.5 h-3.5" />
              Unlocks {unlockCount} task{unlockCount !== 1 ? 's' : ''}
            </span>
          )}

          {/* Due Date */}
          {subtask.due_date && (
            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${
              overdue
                ? 'bg-red-100 text-red-800'
                : daysUntil !== null && daysUntil <= 3
                ? 'bg-orange-100 text-orange-800'
                : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="w-3.5 h-3.5" />
              {overdue && `${Math.abs(daysUntil)}d overdue - `}
              Due {dueDateText}
            </span>
          )}

          {/* Time Estimate */}
          {timeEstimate && (
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              {formatTimeEstimate(timeEstimate)}
            </span>
          )}
        </div>

        {/* Task Tip */}
        {taskTip && (
          <div className="bg-white/80 border border-indigo-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              <span className="font-semibold text-indigo-700">Tip: </span>
              {taskTip}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onStartClick && onStartClick(subtask)}
            className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2 group"
          >
            <span>Start This Task</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          {onAISupportClick && (
            <button
              onClick={() => onAISupportClick(subtask)}
              className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span>AI Help</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NextActionCard;
